// Shared "Last updated" formatters for every stats block on the site, so the
// three of them can't drift apart in wording or timezone.
//
// Fixed timeZone on purpose: these ISO strings are static build-time values, so
// formatting them against the visitor's own clock would produce a different
// string on the server than in the browser and trip a hydration mismatch.
// Date only, no clock time — "7 Aug 2026". The relative part rendered beside it
// (see formatUpdatedAgo) already conveys freshness, so the exact minute added
// noise without telling the reader anything they needed.
export function formatUpdatedAt(iso: string | null): string | null {
  if (!iso) return null;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "medium",
  }).format(new Date(iso));
}

// "33k" / "1.2M" / "121". Every stats block abbreviates counts the same way;
// the exact figure stays reachable on hover via <StatNumber>.
//
// No trailing "+" anywhere. On an abbreviated figure the unit already says the
// number is approximate, so "118k+" stacked two hedges on one number. On an
// unabbreviated one the "+" was simply wrong: 121 campaigns is a complete
// count, not a floor, and the bare number is both cleaner and truer.
// Abbreviation starts at 10,000, not 1,000. Flooring to a single "k" digit
// throws away up to 999 — 2,431 rendered as "2k" understates it by 18%, which
// on a headline figure is worse than the extra characters. From 10k up, the
// discarded remainder is under 10% and shrinking, so the shorter form wins.
export function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.floor(n / 1_000)}k`;
  return n.toLocaleString("en-IN");
}

// A zero means "no live figure yet" — the section's database was unreachable
// when the snapshot was taken — not a real measurement of zero. Render an em
// dash rather than "0+": publishing "0+ Orders Fulfilled" on a live marketing
// page reads as broken, whereas a dash reads as "not available".
export function showCount(n: number): string {
  return n > 0 ? formatCount(n) : "—";
}

// "2 hours ago" / "3 days ago". Reads as a freshness signal rather than a
// timestamp the visitor has to mentally diff against their own clock.
//
// This depends on "now", which differs between build time and view time, so it
// must only ever be called in the browser after mount — see <UpdatedAt>. Never
// render it during SSR or it will hydration-mismatch.
export function formatUpdatedAgo(iso: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.floor((Date.now() - then) / 1000);
  // Clock skew or a snapshot stamped slightly in the future: don't say
  // "-1 minutes ago".
  if (seconds < 60) return "just now";

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "always" });
  for (const [unit, size] of units) {
    if (seconds >= size) return rtf.format(-Math.floor(seconds / size), unit);
  }
  return "just now";
}
